import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
} from '@ifixit/shopify-storefront-client';
import initializeComponent from 'Shared/react-initialize';
import { Tabs, TabsContent } from '@ifixit/ui/components/tabs';
import { useState } from 'react';
import { UserCard } from './Sidebar/user_card';
import { UserTabs, userTabTriggerId } from './user_tabs';
import { UserView } from './user_view';
import {
   faAddressCard,
   faBell,
   faStore,
   faFiles,
   faListTimeline,
   faCertificate,
   faGrid2,
   faCalendarCheck,
} from '@fortawesome/pro-solid-svg-icons';
import type { VoteHistoryDataType } from './vote_history';
import type { UserSummaryType } from './Sidebar/user_card';

export type SidebarDataType = {
   userCardData: UserSummaryType;
   certData?: any;
   voteHistoryData?: VoteHistoryDataType;
};

function getInitialUnreadNotifCount(viewData: any): number {
   for (const viewDatum of viewData) {
      if (viewDatum.name === 'Notifications') {
         return viewDatum.data.notif_count;
      }
   }
   return 0;
}

const UserPage = ({
   userid,
   viewData,
   sidebarData,
   activeTab,
   shopifyStore,
}: {
   userid: number;
   viewData: any[];
   editUrl?: string;
   usersUrl: string;
   sidebarData: SidebarDataType;
   activeTab: string;
   shopifyStore: {
      currencyCode: string;
      country: CountryCode;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
   };
}) => {
   const [currentTab, setCurrentTab] = useState(activeTab);
   const [unreadNotifCount, setUnreadNotifCount] = useState(getInitialUnreadNotifCount(viewData));
   const tabIcons = {
      'About': faAddressCard,
      'Devices': faGrid2,
      'Contributions': faFiles,
      'Activity': faListTimeline,
      'Reputation': faCertificate,
      'Notifications': faBell,
      'Orders': faStore,
      'Subscriptions': faCalendarCheck,
   };

   const layoutClassName =
      'flex max-md:flex-col justify-between gap-8 md:gap-12 mx-auto max-w-[1024px] mt-4 md:mt-16';
   // Routes set activeTab without the gating getViewTabs() applies (/User/Orders
   // without feature-cart, /User/Contributions on meta), so the trigger this
   // panel names is not guaranteed to render.
   const hasTabTrigger = viewData.some((tab: any) => tab.name === currentTab);
   const profileDisplay = (
      <UserProfileDisplay
         userid={userid}
         viewData={viewData}
         currentTab={currentTab}
         setUnreadNotifCount={setUnreadNotifCount}
      />
   );

   if (App.isOnMobileApp) {
      return (
         <ShopifyStorefrontProvider {...shopifyStore}>
            <div className={layoutClassName}>
               <div className="md:flex-[656px]">{profileDisplay}</div>
            </div>
         </ShopifyStorefrontProvider>
      );
   }

   return (
      <ShopifyStorefrontProvider {...shopifyStore}>
         <Tabs value={currentTab} className={layoutClassName}>
            <div className="flex flex-col gap-4 min-lg:w-[320px]">
               <UserCard {...sidebarData.userCardData} />
               <UserTabs
                  viewTabs={viewData}
                  notifBadge={unreadNotifCount}
                  setCurrentTab={setCurrentTab}
                  tabIcons={tabIcons}
               />
            </div>
            <TabsContent
               value={currentTab}
               aria-labelledby={hasTabTrigger ? userTabTriggerId(currentTab) : undefined}
               className="mt-0 md:flex-[656px]"
            >
               {profileDisplay}
            </TabsContent>
         </Tabs>
      </ShopifyStorefrontProvider>
   );
};

const UserProfileDisplay = ({
   userid,
   viewData,
   currentTab,
   setUnreadNotifCount,
}: {
   userid: number;
   viewData: any[];
   currentTab: string;
   setUnreadNotifCount: (count: number) => void;
}) => {
   const selectedTab = viewData.find((tab: any) => tab.name === currentTab);
   return (
      <UserView
         userid={userid}
         currentTab={selectedTab}
         setUnreadNotifCount={setUnreadNotifCount}
      />
   );
};

initializeComponent('UserPage', UserPage);
